import React from 'react';

/**
 * Skeleton loader components for native-feel loading states
 * Never show blank screens - always show intentional loading
 */

// Basic skeleton box
export const Skeleton = ({ className = "", style = {} }) => (
  <div className={`skeleton ${className}`} style={style} />
);

// Text line skeleton
export const SkeletonText = ({ width = "100%", className = "" }) => (
  <div 
    className={`skeleton skeleton-text ${className}`} 
    style={{ width }} 
  />
);

// Small text skeleton
export const SkeletonTextSm = ({ width = "100%", className = "" }) => (
  <div 
    className={`skeleton skeleton-text-sm ${className}`} 
    style={{ width }} 
  />
);

// Avatar skeleton
export const SkeletonAvatar = ({ size = 40, className = "" }) => (
  <div 
    className={`skeleton skeleton-avatar ${className}`} 
    style={{ width: size, height: size }} 
  />
);

// Card skeleton
export const SkeletonCard = ({ height = 100, className = "" }) => (
  <div 
    className={`skeleton skeleton-card ${className}`} 
    style={{ height }} 
  />
);

// Performance page skeleton
export const PerformanceSkeleton = () => (
  <div className="min-h-screen bg-[#F7F8FA]">
    {/* Header skeleton */}
    <div className="bg-gradient-to-br from-[#5B3DF5] to-[#4A2FE0] px-4 py-4">
      <div className="flex items-center justify-between mb-4">
        <Skeleton className="w-10 h-10 rounded-full bg-white/20" />
        <Skeleton className="w-32 h-6 rounded bg-white/20" />
        <Skeleton className="w-10 h-10 rounded-full bg-white/20" />
      </div>
      <div className="bg-white/10 rounded-2xl p-4">
        <Skeleton className="w-24 h-4 rounded bg-white/20 mb-2" />
        <Skeleton className="w-32 h-10 rounded bg-white/20 mb-3" />
        <Skeleton className="w-full h-2 rounded-full bg-white/20" />
      </div>
    </div>
    
    <div className="px-4 -mt-2 space-y-3">
      {/* Actions card skeleton */}
      <SkeletonCard height={120} className="bg-white" />
      
      {/* Stats grid skeleton */}
      <div className="grid grid-cols-2 gap-3">
        <SkeletonCard height={100} className="bg-white" />
        <SkeletonCard height={100} className="bg-white" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <SkeletonCard height={100} className="bg-white" />
        <SkeletonCard height={100} className="bg-white" />
      </div>
    </div>
  </div>
);

// CRM list skeleton
export const CRMListSkeleton = () => (
  <div className="space-y-2 p-4">
    {[1, 2, 3, 4, 5].map((i) => (
      <div key={i} className="bg-white rounded-xl p-3 flex items-center gap-3">
        <SkeletonAvatar size={44} />
        <div className="flex-1">
          <SkeletonText width="60%" className="mb-2" />
          <SkeletonTextSm width="40%" />
        </div>
        <Skeleton className="w-16 h-6 rounded-full" />
      </div>
    ))}
  </div>
);

// Leaderboard skeleton
export const LeaderboardSkeleton = () => (
  <div className="space-y-2 p-4">
    {/* Top 3 podium */}
    <div className="flex justify-center items-end gap-4 py-6">
      <div className="text-center">
        <SkeletonAvatar size={56} className="mx-auto mb-2" />
        <SkeletonText width={60} className="mx-auto" />
      </div>
      <div className="text-center -mt-4">
        <SkeletonAvatar size={72} className="mx-auto mb-2" />
        <SkeletonText width={70} className="mx-auto" />
      </div>
      <div className="text-center">
        <SkeletonAvatar size={56} className="mx-auto mb-2" />
        <SkeletonText width={60} className="mx-auto" />
      </div>
    </div>
    
    {/* List */}
    {[4, 5, 6, 7, 8].map((i) => (
      <div key={i} className="bg-white rounded-xl p-3 flex items-center gap-3">
        <Skeleton className="w-6 h-6 rounded" />
        <SkeletonAvatar size={40} />
        <div className="flex-1">
          <SkeletonText width="50%" className="mb-1" />
          <SkeletonTextSm width="30%" />
        </div>
        <Skeleton className="w-16 h-5 rounded" />
      </div>
    ))}
  </div>
);

// Feed skeleton
export const FeedSkeleton = () => (
  <div className="space-y-3 p-4">
    {[1, 2, 3].map((i) => (
      <div key={i} className="bg-white rounded-2xl p-4">
        <div className="flex items-center gap-3 mb-3">
          <SkeletonAvatar size={40} />
          <div className="flex-1">
            <SkeletonText width="40%" className="mb-1" />
            <SkeletonTextSm width="25%" />
          </div>
        </div>
        <SkeletonText width="100%" className="mb-2" />
        <SkeletonText width="80%" className="mb-2" />
        <SkeletonText width="60%" />
        <div className="flex gap-4 mt-4 pt-3 border-t border-slate-100">
          <Skeleton className="w-12 h-6 rounded" />
          <Skeleton className="w-12 h-6 rounded" />
          <Skeleton className="w-12 h-6 rounded" />
        </div>
      </div>
    ))}
  </div>
);

// Generic list skeleton
export const ListSkeleton = ({ count = 5 }) => (
  <div className="space-y-2 p-4">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="bg-white rounded-xl p-3 flex items-center gap-3">
        <SkeletonAvatar size={40} />
        <div className="flex-1">
          <SkeletonText width={`${60 + Math.random() * 20}%`} className="mb-1" />
          <SkeletonTextSm width={`${30 + Math.random() * 20}%`} />
        </div>
      </div>
    ))}
  </div>
);

export default Skeleton;
